import * as React from "react";
import { useState } from "react";

import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Avatar,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TableRow,
  TextField,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { SelectChangeEvent } from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import FilterAndPaging from "components/FilterAndPaging";
import { rowsPerpageOptions } from "components/Pagination";
import useDebounce from "hooks/useDebounce";
import useOrganizations from "hooks/useOrganizations";
import { nanoid } from "nanoid";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { IFilterOrganizationParams, ILeague } from "utils/api.interfaces";
import { StyledTableCell, StyledTableRowNoPointer } from "utils/common";
import ActionMenu from "../components/ActionMenu";
import { OrganizationIcon } from "../components/brandlibrary/MingleIcons";
import ButtonOverviewDetailLink from "../components/ButtonOverviewDetailLink";
import CountryFilter from "../components/FilterCountry";
import FilterLeague from "../components/FilterLeague";

export default function Organizations() {
  const navigate = useNavigate();

  let [queryParams, setQueryParams] = useSearchParams();
  const initialSearchParams: IFilterOrganizationParams = {
    NameFilter: queryParams.get("NameFilter") || "",
    LeagueId: queryParams.get("LeagueId") || "",
    page: queryParams.get("page") || "0",
    pageSize: queryParams.get("pageSize") || rowsPerpageOptions[3].toString(),
  };
  const [searchParams, setSearchParams] =
    useState<IFilterOrganizationParams>(initialSearchParams);
  const [selectedCountry, setSelectedCountry] = useState<string>("NL");
  const [selectedLeagueId, setSelectedLeagueId] = useState<string>(
    initialSearchParams.LeagueId
  );
  const [orgNameFilter, setOrgNameFilter] = useState<string>(
    initialSearchParams.NameFilter
  );
  const debouncedOrgNameFilter = useDebounce(orgNameFilter, 500);

  const { isLoading, dataOrganizations } = useOrganizations({
    NameFilter: queryParams.get("NameFilter") || "",
    LeagueId: queryParams.get("LeagueId") || "",
    page: queryParams.get("page") || "0",
    pageSize: queryParams.get("pageSize") || rowsPerpageOptions[3].toString(),
  });
  const handleNameFilterChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setOrgNameFilter(event.target.value);
  };
  const handleLeagueChange = (
    event: React.ChangeEvent<unknown>,
    league: ILeague
  ) => {
    if (league) {
      setSelectedLeagueId(league.id);
      handleParamsChange({
        LeagueId: league.id,
        // page: "0",
        // pageSize: rowsPerpageOptions[2].toString(),
      });
    } else {
      setSelectedLeagueId("");
      handleParamsChange({
        LeagueId: "",
        // page: "0",
        // pageSize: rowsPerpageOptions[2].toString(),
      });
    }
  };
  const handleCountryChange = (event: SelectChangeEvent<string | null>) => {
    const newCountry = event.target.value || "";
    setSelectedCountry(newCountry);
    setSelectedLeagueId("");
    handleParamsChange({
      LeagueId: "",
      page: "0",
      pageSize: rowsPerpageOptions[2].toString(),
    });
  };

  const handleParamsChange = (updatedParam: any) => {
    console.log("updatedParam", updatedParam);
    const newParams: any = {
      ...Object.fromEntries(new URLSearchParams(queryParams.toString())),
      ...updatedParam,
    };
    setSearchParams(newParams);
  };

  React.useEffect(() => {
    setQueryParams(searchParams);
  }, [searchParams]);

  React.useEffect(() => {
    if (
      debouncedOrgNameFilter.length === 0 ||
      debouncedOrgNameFilter.length >= 3
    ) {
      handleParamsChange({
        NameFilter: debouncedOrgNameFilter,
      });
    }
    // else do nothing (don't filter until 3+ chars)
  }, [debouncedOrgNameFilter]);

  return (
    <>
      <Typography
        component="h1"
        variant="h1"
        color="inherit"
        noWrap
        gutterBottom
        sx={{
          textAlign: "center",
          margin: "40px 0px",
        }}
      >
        Organizations list & search
      </Typography>

      <>
        <Button
          component={Link}
          size="small"
          variant="contained"
          color="success"
          to={`/organizations/add/`}
          startIcon={<OrganizationIcon />}
        >
          add new organization
        </Button>
        <FilterAndPaging
          data={dataOrganizations}
          filterPagingParams={initialSearchParams}
          // onParamsChange={handleParamsChange}
          onParamsChange={(params) => {
            console.log("params", params);
            // setQueryParams(params);
            handleParamsChange(params);
          }}
          showSearchField={false}
          filterOptions={
            <>
              <TextField
                margin="dense"
                id="filter"
                sx={{
                  minWidth: "300px",
                  // color: selectedLeagueId === "" ? "#888" : "inherit",
                  // "& .MuiInputBase-root": {
                  //   color: selectedLeagueId === "" ? "#888" : "inherit",
                  // },
                }}
                label="Search organization name"
                variant="standard"
                onChange={handleNameFilterChange}
                value={orgNameFilter}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton aria-label="" edge="end">
                        {isLoading ? (
                          <CircularProgress size={15} />
                        ) : (
                          <FilterListIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <FilterLeague
                onLeagueChange={handleLeagueChange}
                initSelectedLeague={selectedLeagueId}
                countryCode={selectedCountry}
              />

              <CountryFilter
                onCountryChange={handleCountryChange}
                initSelectedCountry={selectedCountry}
              />

              <Typography
                variant="body2"
                sx={{
                  marginBottom: 2,
                  color: "#888",
                  fontSize: 12,
                }}
              >
                Country filter only applies to the league filter, not to the
                organizations
              </Typography>
            </>
          }
          TableHeaders={
            <TableRow>
              <StyledTableCell colSpan={8}>organization</StyledTableCell>
              <StyledTableCell align="right">Action</StyledTableCell>
            </TableRow>
          }
        >
          {dataOrganizations?.organizations?.map((row) => (
            <StyledTableRowNoPointer key={nanoid(6)} hover>
              <StyledTableCell colSpan={8}>
                <Stack
                  direction="row"
                  alignContent="center"
                  justifyContent="flex-start"
                  alignItems="center"
                  gap={4}
                >
                  <Avatar
                    sx={{
                      backgroundColor: "#fff",
                      marginRight: "6px",
                      width: "60px",
                      height: "60px",
                    }}
                  >
                    <img
                      src={row.logoUrl}
                      style={{ width: 40, height: "auto" }}
                    />
                  </Avatar>

                  <ButtonOverviewDetailLink
                    link={`/organizations/${row.id}`}
                    title={row.name}
                  />
                </Stack>
              </StyledTableCell>
              <StyledTableCell align="right">
                <ActionMenu
                  itemId={row?.id}
                  handleNewClick={(e) => {
                    navigate(`/organizations/add/`);
                  }}
                  handleEditClick={(e, id) => {
                    navigate(`/organizations/edit/${id}`);
                  }}
                  showDeleteButton={false}
                />
              </StyledTableCell>
            </StyledTableRowNoPointer>
          ))}
        </FilterAndPaging>
      </>
    </>
  );
}
